import { useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { Product } from '../globals'
import { theme } from '../theme'

const sectionTitle: CSSProperties = {
  fontSize: '11px',
  fontWeight: 700,
  color: theme.textMuted,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  margin: '0 0 16px',
}

const card: CSSProperties = {
  backgroundColor: theme.surface,
  border: `1px solid ${theme.border}`,
  borderRadius: '10px',
  padding: '20px 24px',
  marginBottom: '20px',
}

const code = (text: string) => (
  <code
    style={{
      fontFamily: 'ui-monospace, monospace',
      fontSize: '11px',
      color: theme.accent,
      backgroundColor: theme.accentSubtle,
      padding: '1px 5px',
      borderRadius: '3px',
    }}
  >
    {text}
  </code>
)

type StepProps = { n: number; children: ReactNode }

const Step = ({ n, children }: StepProps) => (
  <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
    <div
      style={{
        flexShrink: 0,
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        backgroundColor: theme.accentSubtle,
        border: `1px solid ${theme.accent}`,
        color: theme.accent,
        fontSize: '11px',
        fontWeight: 700,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {n}
    </div>
    <p style={{ fontSize: '13px', color: theme.textMuted, margin: 0, lineHeight: 1.6 }}>
      {children}
    </p>
  </div>
)

export const ProductImpressionPage = () => {
  const [lastTriggered, setLastTriggered] = useState<Product | null>(null)

  const handleImpression = (product: Product) => {
    window.ChiliWebData?.productImpression(product)
    setLastTriggered(product)
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
        <span
          style={{
            fontSize: '10px',
            fontWeight: 700,
            color: theme.accent,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            backgroundColor: theme.accentSubtle,
            padding: '3px 8px',
            borderRadius: '4px',
          }}
        >
          Product API
        </span>
      </div>

      <h1
        style={{
          fontSize: '24px',
          fontWeight: 700,
          color: theme.text,
          margin: '0 0 6px',
          letterSpacing: '-0.02em',
        }}
      >
        Product Impression
      </h1>
      <p style={{ fontSize: '13px', color: theme.textMuted, margin: '0 0 28px' }}>
        Test {code('productImpression(product)')} on {code('window.ChiliWebData')}. Fires a{' '}
        {code('ProductImpression')} event in Jitsu identifying which CP product is deployed on the
        page.
      </p>

      {/* Controls */}
      <div style={card}>
        <p style={sectionTitle}>Controls</p>

        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
          <button
            data-test-id='chat-impression'
            onClick={() => handleImpression('Chat')}
            style={{
              backgroundColor: theme.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '7px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Chat Impression
          </button>

          <button
            data-test-id='concierge-impression'
            onClick={() => handleImpression('Concierge')}
            style={{
              backgroundColor: theme.accent,
              color: '#fff',
              border: 'none',
              borderRadius: '7px',
              padding: '8px 18px',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Concierge Impression
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '13px',
            color: theme.textMuted,
          }}
        >
          <span>Last triggered:</span>
          {lastTriggered ? (
            <span
              style={{
                color: theme.accent,
                fontWeight: 600,
                backgroundColor: theme.accentSubtle,
                padding: '2px 8px',
                borderRadius: '4px',
              }}
            >
              {lastTriggered}
            </span>
          ) : (
            <span style={{ color: theme.textDim }}>none</span>
          )}
        </div>
      </div>

      {/* How to test */}
      <div style={card}>
        <p style={sectionTitle}>How to test</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Step n={1}>
            Make sure the snippet is initialized. Go to{' '}
            <strong style={{ color: theme.text }}>Home</strong> and click{' '}
            <strong style={{ color: theme.text }}>Initialize</strong> to start the snippet.
          </Step>
          <Step n={2}>Open the browser console (DevTools → Console tab).</Step>
          <Step n={3}>
            Click <strong style={{ color: theme.text }}>Chat Impression</strong>. Verify the console
            shows: {code("[jitsu] track ProductImpression { product: 'Chat' }")}
          </Step>
          <Step n={4}>
            Click <strong style={{ color: theme.text }}>Concierge Impression</strong>. Verify the
            console shows: {code("[jitsu] track ProductImpression { product: 'Concierge' }")}
          </Step>
          <Step n={5}>
            Confirm the <strong style={{ color: theme.text }}>Last triggered</strong> status
            indicator updates after each click.
          </Step>
        </div>
      </div>

      {/* Event shape */}
      <div style={card}>
        <p style={sectionTitle}>Event shape</p>
        <pre
          style={{
            margin: 0,
            fontFamily: 'monospace',
            fontSize: '12px',
            color: theme.text,
            backgroundColor: theme.surfaceElevated,
            borderRadius: '8px',
            padding: '14px 16px',
            overflowX: 'auto',
            lineHeight: 1.6,
          }}
        >{`jitsu.track('ProductImpression', {
  product: 'Chat' | 'Concierge',
})`}</pre>
      </div>
    </div>
  )
}
